import { inject, Injectable, signal } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular/standalone';
import { finalize, take } from 'rxjs';
import { Capacitor } from '@capacitor/core';

import { AuthService } from '@core/auth/services/auth.service';
import { BiometricService } from '@core/auth/services/biometric.service';
import { LoadingService } from '@core/services/loading.service';
import { PersonHttpService } from '@features/person/services/person-http.service';
import { PersonStoreService } from '@core/stores/person-store.service';
import { TelemedicineService } from '@features/telemedicine/services/telemedicine.service';
import { TokenStoreService } from '@core/stores/token-store.service';
import { TokenCacheService } from '@core/services/token-cache.service';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { Environment } from '@core/services/environment/models/environment.models';
import { DeepLinkService } from '@core/services/deep-link.service';
import { AppPromoService } from '@shared/services/app-promo/app-promo.service';
import { SignInStateService } from '@core/auth/components/sign-in/services/sign-in-state.service';
import { AcceptPrivacyPolicyModalComponent } from '@core/auth/components/sign-in/components/accept-privacy-policy-modal/accept-privacy-policy-modal.component';
import type { ActionResponse } from '@core/auth/components/sign-in/components/accept-privacy-policy-modal/models/accept-privacy-policy.model';
import { BiometricEnablePromptComponent } from '@core/auth/components/sign-in/components/biometric-enable-prompt/biometric-enable-prompt.component';
import { TelemedicineAccessModalComponent } from '@features/telemedicine/components/telemedicine-access-modal/telemedicine-access-modal.component';
import type { TelemedicineModelActionResponse } from '@features/telemedicine/components/telemedicine-access-modal/models/telemedicine-access.model';
import { TelemedicineAccessModalSource } from '@features/telemedicine/components/telemedicine-access-modal/models/telemedicine-access.enum';
import { AppPromoModalComponent } from '@shared/components/modals/app-promo-modal/app-promo-modal.component';
import type { Person } from '@features/person/models/person.model';

@Injectable({
  providedIn: 'root'
})
export class SignInFlowService {
  private authService = inject(AuthService);
  private biometricService = inject(BiometricService);
  private personHttpService = inject(PersonHttpService);
  private personStore = inject(PersonStoreService);
  private telemedicineService = inject(TelemedicineService);
  private loadingService = inject(LoadingService);
  private tokenStore = inject(TokenStoreService);
  private tokenCache = inject(TokenCacheService);
  private envService = inject(EnvironmentService);
  private modalController = inject(ModalController);
  private navCtrl = inject(NavController);
  private deepLinkService = inject(DeepLinkService);
  private appPromoService = inject(AppPromoService);
  private signInStateService = inject(SignInStateService);

  private loginSucceeded = signal(false);
  readonly loginCompleted = this.loginSucceeded.asReadonly();

  private pendingEmail = '';
  private pendingPassword = '';
  private restoringSession = false;

  login(email: string, password: string, rememberMe: boolean): void {
    this.restoringSession = false;
    this.loginSucceeded.set(false);
    this.loadingService.startLoading('common.sign-in-user');

    this.authService
      .login(email, password, rememberMe)
      .pipe(
        take(1),
        finalize(() => this.loadingService.stopLoading())
      )
      .subscribe((res) => {
        if (res.tokenInfo && res.token) {
          this.pendingEmail = email;
          this.pendingPassword = password;

          const personId = res.tokenInfo.PersonId;
          const currentEnv = this.envService.getCurrentEnvironmentName() as Environment;

          this.tokenStore.updateTokenInfo(res.tokenInfo);
          this.tokenCache.setToken(currentEnv, res.token);

          this.personHttpService
            .getPersonDetails(personId)
            .pipe(take(1))
            .subscribe((person: Person) => {
              this.personStore.updatePerson(person);
              this.checkLegalAcceptance();
            });

          if (res.tokenInfo.CanSwitchEnvironment === 'True') {
            const targetEnv =
              currentEnv === Environment.PRODUCTION ? Environment.DEMO : Environment.PRODUCTION;

            this.authService
              .backgroundLoginForPersonId(email, password, targetEnv)
              .pipe(take(1))
              .subscribe((result) => {
                if (result.token) {
                  this.tokenCache.setToken(targetEnv, result.token);
                }
              });
          }
        }
      });
  }

  // The token is persisted before the user answers the consent modals, so a user who
  // closes the app mid-flow (or declines consent in-app) would otherwise be auto-logged in
  // on relaunch without the server-side acceptance dates ever being re-checked.
  verifyRestoredSession(personId: string): void {
    this.restoringSession = true;

    this.personHttpService
      .getPersonDetails(personId)
      .pipe(take(1))
      .subscribe({
        next: (person: Person) => {
          this.personStore.updatePerson(person);
          this.checkLegalAcceptance();
        },
        error: (error: unknown) => {
          this.restoringSession = false;
          console.error('Failed to verify legal acceptance for restored session:', error);
        }
      });
  }

  async openPrivacyPolicyModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: AcceptPrivacyPolicyModalComponent,
      backdropDismiss: false,
      cssClass: 'centered-dialog-modal'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<ActionResponse>();

    if (data?.accepted) {
      await modal.dismiss();
      this.personHttpService.setPrivacyPolicyDate().subscribe(() => {
        this.personStore.updatePersonProps({ termsAcceptanceTime: new Date().toISOString() });
        if (this.personStore.person()?.privacyConsentTime === null) {
          void this.openTelemedicineAccessModal();
        } else {
          void this.continueAfterLegalAcceptance();
        }
      });
    } else if (data?.logout) {
      await this.logoutFromConsent();
    }
  }

  async openTelemedicineAccessModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: TelemedicineAccessModalComponent,
      componentProps: { source: signal(TelemedicineAccessModalSource.signIn) },
      backdropDismiss: true,
      cssClass: 'centered-dialog-modal'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<TelemedicineModelActionResponse>();

    if (data?.acceptedBoth) {
      this.loadingService.startLoading();
      this.personHttpService
        .setPrivacyConsentDate()
        .pipe(
          take(1),
          finalize(() => this.loadingService.stopLoading())
        )
        .subscribe(() => {
          this.loadingService.startLoading();
          this.personStore.updatePersonProps({ privacyConsentTime: new Date().toISOString() });
          this.telemedicineService
            .createTelemedicineUsers()
            .pipe(
              take(1),
              finalize(() => {
                this.loadingService.stopLoading();
                void this.continueAfterLegalAcceptance();
              })
            )
            .subscribe();
        });
    } else if (data?.continueWithout) {
      void this.continueAfterLegalAcceptance();
    }
  }

  async openAppPromoModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: AppPromoModalComponent,
      backdropDismiss: false,
      cssClass: 'centered-dialog-modal'
    });

    await modal.present();
    await modal.onDidDismiss();
    await this.navigateToRoot();
  }

  private checkLegalAcceptance(): void {
    if (this.personStore.person()?.termsAcceptanceTime === null) {
      void this.openPrivacyPolicyModal();
    } else if (this.personStore.person()?.privacyConsentTime === null) {
      void this.openTelemedicineAccessModal();
    } else {
      void this.continueAfterLegalAcceptance();
    }
  }

  // A restored session is already on its destination route and has no password in memory,
  // so the biometric-enable prompt, app promo and root navigation only apply to a fresh login.
  private async continueAfterLegalAcceptance(): Promise<void> {
    if (this.restoringSession) {
      this.restoringSession = false;
      return;
    }

    await this.openAppPromoModalOrNavigate();
  }

  private async logoutFromConsent(): Promise<void> {
    this.clearPendingCredentials();
    await this.authService.logout();

    if (this.restoringSession) {
      this.restoringSession = false;
      await this.navCtrl.navigateRoot('/auth');
    }
  }

  private async openAppPromoModalOrNavigate(): Promise<void> {
    await this.openBiometricEnableIfNeeded();

    if (this.appPromoService.isPromoEligible()) {
      await this.openAppPromoModal();
    } else {
      await this.navigateToRoot();
    }
  }

  private clearPendingCredentials(): void {
    this.pendingEmail = '';
    this.pendingPassword = '';
  }

  private async openBiometricEnableIfNeeded(): Promise<void> {
    const email = this.pendingEmail;
    const password = this.pendingPassword;
    this.clearPendingCredentials();

    if (!Capacitor.isNativePlatform()) return;
    if (!(await this.biometricService.isAvailable())) return;
    if (this.biometricService.isEnabled()) return;
    if (this.biometricService.hasDeclined()) return;

    // Defensive guard: if counter is at or beyond the limit (e.g. corrupted state),
    // permanently decline and skip the prompt.
    if (this.biometricService.hasReachedPromptLimit()) {
      await this.biometricService.disable();
      return;
    }

    const modal = await this.modalController.create({
      component: BiometricEnablePromptComponent,
      backdropDismiss: false,
      cssClass: 'centered-dialog-modal'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss<{ enabled: boolean }>();

    if (data?.enabled) {
      await this.biometricService.enable(email, password);
    } else {
      await this.biometricService.recordPromptDismissal();
    }
  }

  private async navigateToRoot(): Promise<void> {
    this.loginSucceeded.set(true);
    this.signInStateService.clearFormData();

    const hadPendingRedirect = await this.deepLinkService.handlePendingRedirect();

    if (!hadPendingRedirect) {
      await this.navCtrl.navigateRoot('/');
    }
  }
}
